import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";

type CB = { onSuccess?: (r: any) => void; onError?: (e: any) => void };

// ── Suppliers ──────────────────────────────────────────────────────────────

export const useSupplierList = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CI_SUPPLIERS(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CI_SUPPLIERS(cid), { params }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useSupplierCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_SUPPLIER_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_SUPPLIERS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useSupplierUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_SUPPLIER_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_SUPPLIERS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useSupplierDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CI_SUPPLIER_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_SUPPLIERS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Part Categories ────────────────────────────────────────────────────────

export const useCategoryList = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CI_CATEGORIES(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CI_CATEGORIES(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useCategoryCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_CATEGORY_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_CATEGORIES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useCategoryUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_CATEGORY_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_CATEGORIES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useCategoryDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CI_CATEGORY_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_CATEGORIES(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Parts ──────────────────────────────────────────────────────────────────

export const usePartList = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CI_PARTS(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CI_PARTS(cid), { params }),
    select: (r: any) => ({ data: r.data.data as any[], total: r.data.extra?.total ?? 0 }),
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const usePartCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_PART_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_PARTS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const usePartUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_PART_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_PARTS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const usePartDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CI_PART_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_PARTS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Stock Locations ────────────────────────────────────────────────────────

export const useLocationList = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CI_LOCATIONS(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CI_LOCATIONS(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useLocationCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_LOCATION_CREATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_LOCATIONS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useLocationUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_LOCATION_UPDATE(cid), data),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_LOCATIONS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

export const useLocationDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CI_LOCATION_DELETE(cid), { id }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_LOCATIONS(cid)] }); cb.onSuccess?.(r); },
    onError: cb.onError,
  });
};

// ── Stock Levels + Movements ───────────────────────────────────────────────

export const useStockLevels = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CI_LEVELS(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CI_LEVELS(cid), { params }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useStockMovements = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CI_MOVEMENTS(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CI_MOVEMENTS(cid), { params }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useMovementCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CI_MOVEMENT_CREATE(cid), data),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_MOVEMENTS(cid)] });
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CI_LEVELS(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};
